import React, { Fragment } from 'react'
import { Dropdown } from '../../model/general'
import {
	variantTypes,
	parseTemplateStringIntoSegments,
	TemplateSegmentType,
	getTemplateKeys,
} from '@mission.io/question-toolkit'
import { PdfViews } from '@mission.io/question-views'
import { createStyles } from './pdfStyles'
import { theme } from '@mission.io/styles'
import { PdfContextProps } from './types'
import OptionalPDFMarkdown from './OptionalPDFMarkdown'
import {
	CLOZE_DROPDOWN_PDF_MARKDOWN_OVERRIDE,
	ClozeDropdownProvider,
	getTemplateMarkdownString,
} from '../cloze-dropdown/helpers'
import { Style } from '@react-pdf/types'
import {
	escapeMarkdownCharacters,
	getMarkdownString,
} from '../../components/Markdown/hooks'

const questionTypesToRender = {
	[variantTypes.MULTIPLE_CHOICE]: CurriculumMultipleChoice,
	[variantTypes.MULTIPLE_SELECT]: CurriculumMultipleChoice,
	[variantTypes.FREE_RESPONSE]: CurriculumFreeResponse,
	[variantTypes.CLOZE_DROPDOWN]: CurriculumClozeDropdown,
	[variantTypes.IMAGE_LABELING]: CurriculumImageLabeling,
	// TODO: add close reading question support to PDFs
	[variantTypes.CLOSE_READING]: (props: Props) => {
		const Text = props.ReactPdf.Text
		return <Text>Close reading questions are not supported in pdfs.</Text>
	},
}

/**
 * @returns A Pdf view for a single question
 */
export function Question(
	props: PdfViews.QuestionProps & {
		pdfStyles?: PdfContextProps['pdfStyles']
	}
): JSX.Element {
	const Component = questionTypesToRender[props.question.type]

	if (!Component) {
		throw new Error('Invalid question provided')
	}

	const fontSize = (() => {
		const stylesArray = Array.isArray(props.style)
			? props.style
			: [props.style || {}]
		let fontSize

		for (const style of stylesArray) {
			if (style.fontSize) {
				fontSize = style.fontSize
			}
		}

		return fontSize
	})()

	return (
		<Component
			{...{
				...props,
				pdfStyles:
					props.pdfStyles ||
					createStyles(props.ReactPdf.StyleSheet, { fontSize }),
			}}
		/>
	)
}

/**
 * Displays the provided questions as a grid. Each question is given a border
 * @param props
 * @param props.questions The questions to display
 * @param {string} [props.anchorPrefix=pdf-question] The prefix to use for the anchors (ids) on each question. The element for each
 *                                                       question will have the id `${anchorPrefix}${index}`
 * @param props.questionPrefix A prefix to display before each question. If provided, the prefix will be followed by the question number.
 *                     If not provided, no prefix will be displayed.
 * @param props.ReactPdf All exports from the react-pdf library
 * @returns JSX.Element
 */
export function QuestionGrid({
	questions,
	anchorPrefix = 'pdf-question',
	ReactPdf,
	questionPrefix,
}: PdfViews.GridProps): JSX.Element {
	const pdfStyles = createStyles(ReactPdf.StyleSheet)

	return (
		<ReactPdf.View style={pdfStyles.grid}>
			{questions.map((question, index) => {
				return (
					<Question
						ReactPdf={ReactPdf}
						pdfStyles={pdfStyles}
						id={`${anchorPrefix}${index}`}
						question={question}
						key={String(question._id)}
						questionPrefix={
							questionPrefix ? questionPrefix + (index + 1) : undefined
						}
						style={[
							// @ts-expect-error TS2322 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
							index % 2 === 0 ? pdfStyles.gridColumn1 : pdfStyles.gridColumn2,
							{
								padding: `8 16`,
								border: `3 solid ${theme.primaryDark}`,
								borderRadius: `8`,
							},
						]}
					/>
				)
			})}
		</ReactPdf.View>
	)
}

type Props = PdfViews.QuestionProps & {
	pdfStyles: ReturnType<typeof createStyles>
}

type PromptOrFormattedPrompt =
	| {
			prompt: string
	  }
	| {
			formattedPrompt: JSX.Element
	  }

/**
 * A standard wrapper for all questions. Provides a consistent way to display the question prompt/image.
 * Displays the question prompt, the question image, and then any children provided.
 * @param props
 * @param props.imageUrl The url of the image to display
 * @param props.prompt The prompt to display. Can be markdown
 * @param props.formattedPrompt If provided, this will be displayed instead of the `prompt`. This will be displayed as-is without any more formatting.
 */
function QuestionWrapper({
	ReactPdf,
	pdfStyles,
	questionPrefix,
	children,
	imageUrl,
	...props
}: Props &
	PromptOrFormattedPrompt & {
		imageUrl?: string | null
	}) {
	// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
	const fontSize = pdfStyles.textSize.fontSize

	return (
		<ReactPdf.View {...props} wrap={false}>
			<ReactPdf.View
				style={{
					marginBottom: children
						? typeof fontSize === 'number'
							? fontSize * 0.75
							: 8
						: 0,
				}}>
				<ReactPdf.View style={[pdfStyles.textSize]}>
					{'formattedPrompt' in props ? (
						props.formattedPrompt
					) : (
						<OptionalPDFMarkdown
							interpretAsMarkdown
							ReactPdf={ReactPdf}
							pdfStyles={pdfStyles}>
							{getQuestionPromptMarkdown(props.prompt, questionPrefix)}
						</OptionalPDFMarkdown>
					)}
				</ReactPdf.View>
				{imageUrl && <ReactPdf.Image src={imageUrl} style={pdfStyles.image} />}
			</ReactPdf.View>
			{children}
		</ReactPdf.View>
	)
}

/**
 * A PDF view for the free response question
 */
function CurriculumFreeResponse(props: Props) {
	const { question } = props
	if (question.type !== variantTypes.FREE_RESPONSE) {
		return null
	}
	// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
	const imageUrl = question.media?.length ? question.media[0].url : null

	return (
		<QuestionWrapper
			{...{
				...props,
				prompt: question.phrase,
				imageUrl,
			}}
		/>
	)
}

/**
 * A Pdf view for the multiple choice question
 */
function CurriculumMultipleChoice(props: Props) {
	const { question, ReactPdf, pdfStyles } = props
	if (
		question.type !== variantTypes.MULTIPLE_CHOICE &&
		question.type !== variantTypes.MULTIPLE_SELECT
	) {
		return null
	}
	// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
	const questionImage = question.media?.length ? question.media[0].url : null

	return (
		<QuestionWrapper
			{...{
				...props,
				prompt: question.phrase,
				imageUrl: questionImage,
			}}>
			{question.choices.map((choice, index) => (
				<QuestionAnswer
					key={String(choice._id)}
					pdfStyles={pdfStyles}
					ReactPdf={ReactPdf}
					answer={choice.text}
					isCorrect={choice.answer}
					image={choice.media?.length ? choice.media?.[0]?.url : null}
					index={index}
				/>
			))}
		</QuestionWrapper>
	)
}

/**
 * A Pdf view for the cloze dropdown question
 */
function CurriculumClozeDropdown(props: Props) {
	const { question, ReactPdf, pdfStyles, questionPrefix } = props

	if (question.type !== variantTypes.CLOZE_DROPDOWN) {
		return null
	}

	const template = `\\markdown ${getQuestionPromptMarkdown(
		question.template,
		questionPrefix
	)}`

	const markdownString = getTemplateMarkdownString(template)

	function ClozeDropdownButton({
		dropdownId,
	}: {
		dropdownId: string
	}): JSX.Element {
		return (
			<ReactPdf.Text
				style={[
					pdfStyles.tag,
					{
						backgroundColor: theme.primary,
					},
				]}>
				&nbsp;{dropdownId}&nbsp;
			</ReactPdf.Text>
		)
	}

	// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
	const questionImage = question.media?.length ? question.media[0].url : null

	const renderTemplate = () => {
		if (markdownString != null) {
			const dropdownLookup: Record<string, JSX.Element> = {}
			getTemplateKeys(template).forEach(dropdownId => {
				dropdownLookup[dropdownId] = ClozeDropdownButton({ dropdownId })
			})

			return (
				<ClozeDropdownProvider state={{ dropDowns: dropdownLookup }}>
					<ReactPdf.Text
						style={{
							lineHeight: 1.3,
						}}>
						<OptionalPDFMarkdown
							interpretAsMarkdown
							pdfStyles={pdfStyles}
							ReactPdf={ReactPdf}
							overrides={CLOZE_DROPDOWN_PDF_MARKDOWN_OVERRIDE}>
							{markdownString}
						</OptionalPDFMarkdown>
					</ReactPdf.Text>
				</ClozeDropdownProvider>
			)
		}
		return (
			<ReactPdf.Text>
				{parseTemplateStringIntoSegments(template).map((segment, index) => {
					if (segment.type === TemplateSegmentType.Input) {
						return (
							<ReactPdf.Text key={`${index}`} style={pdfStyles.tagWrapper}>
								{ClozeDropdownButton({ dropdownId: segment.inputId })}
							</ReactPdf.Text>
						)
					}
					if (segment.type === TemplateSegmentType.Text) {
						return (
							<ReactPdf.Text key={`${index}`}>{segment.text}</ReactPdf.Text>
						)
					}
					return null
				})}
			</ReactPdf.Text>
		)
	}

	return (
		<QuestionWrapper
			{...{
				...props,
				formattedPrompt: renderTemplate(),
				imageUrl: questionImage,
			}}>
			<Dropdowns
				pdfStyles={pdfStyles}
				ReactPdf={ReactPdf}
				dropdowns={question.dropdowns}
			/>
		</QuestionWrapper>
	)
}

/**
 * A Pdf view for the image labeling question
 */
function CurriculumImageLabeling(props: Props) {
	const { question, ReactPdf, pdfStyles } = props
	if (question.type !== variantTypes.IMAGE_LABELING) {
		return null
	}
	const image = question.media[0]?.url
	const circleRadius = Math.floor(0.038 * question.imageProperties.width)
	return (
		<QuestionWrapper
			{...{
				...props,
				prompt: question.phrase,
			}}>
			<ReactPdf.View style={{ textAlign: 'center', paddingBottom: 8 }}>
				<ReactPdf.Svg
					viewBox={`0 0 ${question.imageProperties.width} ${question.imageProperties.height}`}>
					<ReactPdf.Image
						src={image}
						x={0}
						y={0}
						style={{
							width: question.imageProperties.width,
							height: question.imageProperties.height,
						}}
					/>
					{question.dropdowns.map((item, index: number) => {
						const x = item.x
						const y = item.y
						const value = String(index + 1)
						return (
							<Fragment key={`item-${index}`}>
								<ReactPdf.Circle
									cx={x}
									cy={y}
									r={circleRadius}
									fill={theme.primary}
									strokeWidth={1}
									stroke={theme._cloudBlue}
								/>
								<ReactPdf.Text
									x={x}
									y={y + Math.floor(0.01 * question.imageProperties.width)}
									style={{ fontSize: circleRadius + 1 }}
									fill={theme._cloudBlue}
									strokeWidth={1}
									textAnchor="middle">
									{value}
								</ReactPdf.Text>
							</Fragment>
						)
					})}
				</ReactPdf.Svg>
			</ReactPdf.View>
			<Dropdowns
				pdfStyles={pdfStyles}
				ReactPdf={ReactPdf}
				dropdowns={question.dropdowns}
			/>
		</QuestionWrapper>
	)
}

/**
 * Gets the markdown representation of the given question prompt. If a question prefix is provided, it will be bolded and prepended to the prompt
 * @param questionPrompt The prompt to get markdown for
 * @param questionPrefix The prefix to prepend to the prompt
 * @returns The markdown representation of the question prompt
 */
function getQuestionPromptMarkdown(
	questionPrompt: string,
	questionPrefix?: string
) {
	return (
		(questionPrefix ? `**${questionPrefix}:** ` : '') +
		(getMarkdownString(questionPrompt) ??
			escapeMarkdownCharacters(questionPrompt))
	)
}

/**
 * Displays dropdowns for a dropdown question in the pdf
 * @param {PdfContextProps & {dropdowns: Dropdown[]}} props props for the component
 * @returns React PDF View
 */
function Dropdowns({
	ReactPdf,
	pdfStyles,
	dropdowns,
}: { dropdowns: Dropdown[] } & PdfContextProps) {
	return (
		<ReactPdf.View style={pdfStyles.grid}>
			{dropdowns.map((dropdown, index) => {
				return (
					<ReactPdf.View
						key={`${index}-dropdown`}
						style={[
							pdfStyles.bordered,
							index % 2 === 0 ? pdfStyles.gridColumn1 : pdfStyles.gridColumn2,
							pdfStyles.tr,
							pdfStyles.roundedSm,
							{ width: '47%', marginBottom: 15 },
						]}>
						<ReactPdf.View
							style={[
								pdfStyles.tagWrapper,
								{
									marginLeft: -7,
									marginTop: -6,
								},
							]}>
							<ReactPdf.Text style={pdfStyles.tag}>{index + 1}</ReactPdf.Text>
						</ReactPdf.View>
						<ReactPdf.View
							style={{
								padding: `8 2`,
								flex: 1,
							}}>
							{dropdown.choices.map((choice, index) => (
								<QuestionAnswer
									pdfStyles={pdfStyles}
									ReactPdf={ReactPdf}
									key={String(choice._id)}
									answer={choice.text}
									isCorrect={choice.answer}
									index={index}
								/>
							))}
						</ReactPdf.View>
					</ReactPdf.View>
				)
			})}
		</ReactPdf.View>
	)
}

const ALPHABET: string[] = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
/**
 * A component to display the answer to a multiple choice question
 */
function QuestionAnswer({
	pdfStyles,
	ReactPdf,
	answer,
	image,
	isCorrect,
	index,
}: {
	answer: string
	image?: string | null
	isCorrect: boolean
	index: number
} & PdfContextProps) {
	const textStyle: Style = {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		lineHeight: 1.3,
	}
	if (isCorrect) {
		textStyle.color = theme.success
	}
	return (
		<ReactPdf.View>
			<ReactPdf.View style={[textStyle, pdfStyles.textSize]}>
				<OptionalPDFMarkdown
					ReactPdf={ReactPdf}
					pdfStyles={pdfStyles}
					interpretAsMarkdown
					color={textStyle.color}>
					{`**${ALPHABET[index % ALPHABET.length]} -** ${getMarkdownString(
						answer
					) ?? escapeMarkdownCharacters(answer)} ${
						isCorrect ? '(Correct)' : ''
					}`}
				</OptionalPDFMarkdown>
			</ReactPdf.View>
			{image && <ReactPdf.Image src={image} style={pdfStyles.image} />}
		</ReactPdf.View>
	)
}
